import 'antd/dist/antd.less'
import '../styles/app.scss'

import { ConfigProvider } from 'antd'
import * as React from 'react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { isIE } from 'react-device-detect'
import { IntlProvider } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Routes as Switch } from 'react-router-dom'

import AppLocale from '../lngProvider/index'
import PageLoadingView from './components/UI-Elements/Feedback/loading/PageLoading'
import LoginPageLayout from './components/UI-Elements/Layout/LoginPageLayout'
import { Dispatchable } from './constants/GlobalTypes'
import { Routes } from './constants/Routes'
import PageScaffold from './pages/HOC/PageScaffold'
import { authenticateUser, authenticateUserWithLoginToken } from './redux/actions/User'
import MainRoutes from './routes/MainRoutes'
import PrivateRoute from './routes/PrivateRoutes'
import IntlService from './services/IntlService'
import { urlLoginToken } from './services/urlLoginToken'
import { getBaseRoutePath, retry } from './util/Utilities'

const LoginPage = lazy(() => retry(() => import('./pages/Login/Views/LoginPage')))
const ResetPasswordPage = lazy(() =>
  retry(() => import('./pages/ResetPassword/Views/ResetPasswordPage'))
)
const AccountsPage = lazy(() => retry(() => import('./pages/AccountsPage')))

const App: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true)
  const { langCode } = useSelector<AppState, UserReducerState>((state) => state.user)
  const dispatch = useDispatch<Dispatchable>()

  useEffect(() => {
    if (urlLoginToken) {
      dispatch(authenticateUserWithLoginToken(urlLoginToken))
        .then(() => {
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    } else {
      dispatch(authenticateUser())
        .then(() => {
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }, [authenticateUser, authenticateUserWithLoginToken])

  // TODO: Change this with locale info from backend
  const { locale, messages, antLocale } =
    AppLocale[IntlService.isLanguageValid(langCode) ? langCode : 'de']

  useEffect(() => {
    IntlService.loadLanguage()
    IntlService.setLocale(locale)
  }, [locale])

  if (isIE) {
    return (
      <h1 className="center-content mt-5">
        Bitte aktualisieren Sie den Browser. <br />
        Bitte verwenden Sie Microsoft Edge anstelle des Internet Explorers
      </h1>
    )
  }

  if (loading) {
    return <PageLoadingView />
  }

  return (
    <IntlProvider
      textComponent={React.Fragment}
      locale={locale}
      messages={messages}
      defaultLocale="de">
      <ConfigProvider form={{ validateMessages: messages.formValidation }} locale={antLocale}>
        <Switch>
          <Route
            key="login-page"
            path={Routes.Authentication.collection().login}
            element={
              <Suspense fallback={null}>
                <LoginPageLayout>
                  <LoginPage />
                </LoginPageLayout>
              </Suspense>
            }
          />
          <Route
            key="accounts-page"
            path={Routes.Authentication.collection().account}
            element={
              <PageScaffold>
                <AccountsPage />
              </PageScaffold>
            }
          />
          <Route
            key="reset-password-page"
            path={Routes.Authentication.collection().reset_password}
            element={
              <PrivateRoute>
                <Suspense fallback={null}>
                  <ResetPasswordPage />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            key="main-pages"
            path={getBaseRoutePath('')}
            element={
              <PrivateRoute>
                <PageScaffold>
                  <MainRoutes />
                </PageScaffold>
              </PrivateRoute>
            }
          />
        </Switch>
      </ConfigProvider>
    </IntlProvider>
  )
}

export default App
